import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  RecommendationsService,
  MerchantProfile,
  DailyRecommendation,
  ProductRecommendation,
} from 'src/app/services/recommendation.service';
import { PlanFeatureService } from 'src/app/services/plan-feature.service';
import { FEATURES } from 'src/app/shared/config/plan-features';
import { FeatureLockComponent } from 'src/app/shared/components/feature-lock/feature-lock.component';
import { AlertPreviewComponent } from 'src/app/shared/components/alert-preview/alert-preview.component';
import {
  UnifiedAlertService,
  UnifiedAlert,
} from 'src/app/services/unified-alert.service';

interface WeekComparison {
  items: { current: number; previous: number; trend: number };
  revenue: { current: number; previous: number; trend: number };
  confidence: { current: number; previous: number; trend: number };
}

interface QuickAction {
  id: string;
  label: string;
  icon: string;
  action: () => void;
}

@Component({
  selector: 'app-recommendations-fixed',
  imports: [CommonModule, FormsModule, AlertPreviewComponent],
  templateUrl: './recommendations-fixed.component.html',
  styleUrls: ['./recommendations-fixed.component.css'],
})
export class RecommendationsFixedComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  readonly FEATURES = FEATURES;
  readonly planFeatureService = inject(PlanFeatureService);
  readonly alertService = inject(UnifiedAlertService);

  merchantProfile: MerchantProfile | null = null;

  // Recommandations hebdomadaires
  weeklyRecommendations: DailyRecommendation[] = [];
  expandedDays: { [key: string]: boolean } = {};

  // Nouveaux: Comparaison N-1
  weekComparison: WeekComparison | null = null;
  showComparison = false;

  // Alertes unifiées (météo + événements + calendrier)
  dayAlerts: UnifiedAlert[] = [];

  // Nouveaux: Actions rapides
  quickActions: QuickAction[] = [];

  // États UI
  isLoading = true;
  isExporting = false;
  validatedDays: Set<string> = new Set();

  constructor(
    private router: Router,
    private recommendationsService: RecommendationsService,
  ) {}

  ngOnInit() {
    this.initQuickActions();
    this.loadMerchantProfile();
  }

  initQuickActions() {
    this.quickActions = [
      {
        id: 'validate-all',
        label: 'Tout valider',
        icon: '✅',
        action: () => this.validateAllDays(),
      },
      {
        id: 'export-pdf',
        label: 'Exporter PDF',
        icon: '📄',
        action: () => this.exportToPdf(),
      },
      {
        id: 'adjust-quantities',
        label: 'Ajustement global',
        icon: '⚙️',
        action: () => this.openGlobalAdjustment(),
      },
      {
        id: 'compare-week',
        label: 'Comparer N-1',
        icon: '📊',
        action: () => this.toggleComparison(),
      },
    ];
  }

  loadMerchantProfile() {
    this.recommendationsService.getMerchantProfile().subscribe((profile) => {
      this.merchantProfile = profile;

      // Note: pas de redirection ici, le routage est géré par le header

      this.loadWeeklyRecommendations();
      this.loadDayAlerts();
      this.loadWeekComparison();
      this.cdr.markForCheck();
    });
  }

  loadWeeklyRecommendations() {
    this.isLoading = true;
    this.recommendationsService.getWeeklyRecommendations().subscribe({
      next: (recs) => {
        this.weeklyRecommendations = recs;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ========== Alertes unifiées (via UnifiedAlertService) ==========

  loadDayAlerts() {
    const lat = this.merchantProfile?.location?.lat;
    const lng = this.merchantProfile?.location?.lng;
    const city = this.merchantProfile?.location?.city || '';
    if (!lat || !lng) {
      this.dayAlerts = [];
      return;
    }
    this.alertService.loadAlerts(lat, lng, city).subscribe((alerts) => {
      this.dayAlerts = alerts;
      this.cdr.markForCheck();
    });
  }

  loadWeekComparison() {
    // Charger les données de la semaine N-1 via l'API pour comparaison réelle
    const prevDates: string[] = [];
    for (let i = 7; i < 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      prevDates.push(d.toISOString().split('T')[0]);
    }

    const requests = prevDates.map((date) =>
      this.recommendationsService
        .getFixedRecommendationsFromAPI(date)
        .pipe(
          catchError(() => of({ success: false, date, recommendations: [] })),
        ),
    );

    forkJoin(requests).subscribe((results: any[]) => {
      let prevItems = 0;
      let prevRevenue = 0;
      let prevConfidenceSum = 0;
      let prevConfidenceCount = 0;

      for (const res of results) {
        if (!res.success || !res.recommendations) continue;
        for (const rec of res.recommendations) {
          for (const p of rec.products || []) {
            const qty = p.recommendedQuantity || p.recommended || 0;
            prevItems += qty;
            prevRevenue += qty * (p.price || 0);
            const conf = p.confidenceScore || p.confidence || 0;
            if (conf) {
              prevConfidenceSum += conf;
              prevConfidenceCount++;
            }
          }
        }
      }

      const prevConfidence =
        prevConfidenceCount > 0
          ? Math.round(prevConfidenceSum / prevConfidenceCount)
          : 0;

      this.weekComparison = {
        items: {
          current: this.getWeeklyTotalItems(),
          previous: prevItems,
          trend: this.calculateTrend(this.getWeeklyTotalItems(), prevItems),
        },
        revenue: {
          current: this.getWeeklyRevenue(),
          previous: Math.round(prevRevenue),
          trend: this.calculateTrend(
            this.getWeeklyRevenue(),
            Math.round(prevRevenue),
          ),
        },
        confidence: {
          current: this.getWeeklyConfidence(),
          previous: prevConfidence,
          trend: this.calculateTrend(
            this.getWeeklyConfidence(),
            prevConfidence,
          ),
        },
      };
      this.cdr.markForCheck();
    });
  }

  calculateTrend(current: number, previous: number): number {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  // ========== Actions jour ==========
  toggleDayDetails(date: string) {
    this.expandedDays[date] = !this.expandedDays[date];
  }

  validateDay(date: string) {
    this.validatedDays.add(date);
    // TODO: Appeler API pour enregistrer la validation
  }

  isValidated(date: string): boolean {
    return this.validatedDays.has(date);
  }

  adjustProductQuantity(
    dayDate: string,
    serviceIndex: number,
    productIndex: number,
    newQty: number,
  ) {
    const day = this.weeklyRecommendations.find((d) => d.date === dayDate);
    if (day && day.services[serviceIndex]?.products[productIndex]) {
      day.services[serviceIndex].products[productIndex].recommended = newQty;
    }
  }

  // ========== Actions globales ==========
  validateAllDays() {
    this.weeklyRecommendations.forEach((day) => {
      this.validatedDays.add(day.date);
    });
  }

  toggleComparison() {
    this.showComparison = !this.showComparison;
  }

  exportToPdf() {
    this.isExporting = true;
    // Simulation export
    setTimeout(() => {
      this.isExporting = false;
      alert('✅ Export PDF généré ! (simulation)');
    }, 1500);
  }

  openGlobalAdjustment() {
    // TODO: Ouvrir modal d'ajustement global (+10%, -10%, etc.)
    alert('Fonctionnalité ajustement global à implémenter');
  }

  // ========== Calculs KPI ==========
  getWeeklyTotalItems(): number {
    return this.weeklyRecommendations.reduce((total, day) => {
      return total + day.services.reduce((sum, s) => sum + s.totalItems, 0);
    }, 0);
  }

  getWeeklyRevenue(): number {
    return Math.round(
      this.weeklyRecommendations.reduce((total, day) => {
        return total + day.services.reduce((sum, s) => sum + s.totalValue, 0);
      }, 0),
    );
  }

  getWeeklyConfidence(): number {
    const total = this.weeklyRecommendations.reduce((sum, day) => {
      return (
        sum + day.services.reduce((s, service) => s + service.confidence, 0)
      );
    }, 0);
    const count = this.weeklyRecommendations.reduce(
      (c, day) => c + day.services.length,
      0,
    );
    return count > 0 ? Math.round(total / count) : 0;
  }

  // ========== Utilitaires affichage ==========
  getPeriodIcon(period: string): string {
    const icons: { [key: string]: string } = {
      'Petit-déjeuner': '🌅',
      Déjeuner: '☀️',
      Dîner: '🌆',
    };
    return icons[period] || '⏰';
  }

  getPeriodLabel(period: string): string {
    return period;
  }

  getWeatherIcon(icon: string): string {
    const icons: { [key: string]: string } = {
      sun: '☀️',
      cloud: '☁️',
      rain: '🌧️',
    };
    return icons[icon] || '🌤️';
  }

  getTrendIcon(trend: number): string {
    if (trend > 0) return '📈';
    if (trend < 0) return '📉';
    return '➡️';
  }

  getTrendClass(trend: number): string {
    if (trend > 0) return 'text-green-600';
    if (trend < 0) return 'text-red-600';
    return 'text-gray-600';
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }

  formatShortDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
    });
  }
}
